import api from '../utils/api';

/**
 * Service for managing food donation requests
 */
const requestService = {
  /**
   * Get all requests for the authenticated user
   * @param {Object} params - Query parameters (limit, offset)
   * @returns {Promise} - API response with requests
   */
  getAllRequests: async (params = {}) => {
    try {
      const response = await api.get('/requests', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch requests' };
    }
  },

  /**
   * Get a request by ID
   * @param {string} id - Request ID
   * @returns {Promise} - API response with request details
   */
  getRequestById: async (id) => {
    try {
      const response = await api.get(`/requests/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch request details' };
    }
  },

  /**
   * Create a new request for a food listing (recipient only)
   * @param {Object} requestData - Request data
   * @returns {Promise} - API response
   */
  createRequest: async (requestData) => {
    try {
      const response = await api.post('/requests', requestData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create request' };
    }
  },

  /**
   * Approve a request (donor only)
   * @param {string} id - Request ID
   * @returns {Promise} - API response
   */
  approveRequest: async (id) => {
    try {
      const response = await api.put(`/requests/${id}/approve`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to approve request' };
    }
  },

  /**
   * Reject a request (donor only)
   * @param {string} id - Request ID
   * @param {Object} data - Optional rejection reason
   * @returns {Promise} - API response
   */
  rejectRequest: async (id, data = {}) => {
    try {
      const response = await api.put(`/requests/${id}/reject`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to reject request' };
    }
  },

  /**
   * Mark a request as fulfilled (donor only)
   * @param {string} id - Request ID
   * @returns {Promise} - API response
   */
  fulfillRequest: async (id) => {
    try {
      const response = await api.put(`/requests/${id}/fulfill`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fulfill request' };
    }
  },

  /**
   * Cancel a request (recipient only)
   * @param {string} id - Request ID
   * @returns {Promise} - API response
   */
  cancelRequest: async (id) => {
    try {
      const response = await api.put(`/requests/${id}/cancel`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to cancel request' };
    }
  },

  /**
   * Get all requests for a specific listing
   * @param {string} listingId - Listing ID
   * @param {Object} params - Query parameters (limit, offset)
   * @returns {Promise} - API response with requests
   */
  getRequestsByListing: async (listingId, params = {}) => {
    try {
      const response = await api.get(`/requests/listing/${listingId}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch listing requests' };
    }
  },

  /**
   * Get requests made by the authenticated recipient
   * @param {Object} params - Query parameters (limit, offset)
   * @returns {Promise} - API response with requests
   */
  getMyRecipientRequests: async (params = {}) => {
    console.log('[requestService] Attempting to fetch recipient requests');
    console.log('[requestService] Current user role:', localStorage.getItem('userRole') || 'Not set');
    
    // IMPORTANT: Our axios instance already has baseURL set to 'http://localhost:8787/api'
    // So we should NOT include the '/api' prefix in our paths
    const pathsToTry = [
      '/requests/my-requests',      // Correct path: localhost:8787/api/requests/my-requests
      '/requests/recipient',        // Alternative path 
      '/test/recipient-requests',   // Debug testing endpoint
      'my-requests'                // Direct path for legacy support
    ];
    
    console.log('[requestService] Will try these paths:', pathsToTry);
    
    // Try each path until one works
    for (let i = 0; i < pathsToTry.length; i++) {
      const path = pathsToTry[i];
      try {
        console.log(`Attempting to fetch recipient requests from: ${path}`);
        const response = await api.get(path, { params });
        console.log(`Successfully fetched recipient requests from: ${path}`);
        // Remember this successful path for future calls
        requestService.recipientRequestsPath = path;
        return response.data;
      } catch (pathError) {
        console.log(`Path ${path} failed with:`, pathError.message);
        // Continue to next path
      }
    }
    
    // If all paths failed, use mock data as fallback
    console.error('Error fetching recipient requests: All paths failed');
    
    // DEVELOPMENT FALLBACK: Return mock data when API fails
    // This allows frontend development to continue when backend is not ready
    console.info('Using mock data for recipient requests as fallback');
    const mockRequests = [
      {
        id: 'mock-req-1',
        listing_id: 'listing-123',
        listing_title: 'Fresh Vegetables',
        recipient_id: 'user-456',
        status: 'pending',
        quantity: 5,
        quantity_unit: 'kg',
        created_at: new Date(Date.now() - 3600000).toISOString(),
        updated_at: new Date(Date.now() - 3600000).toISOString()
      },
      {
        id: 'mock-req-2',
        listing_id: 'listing-456',
        listing_title: 'Canned Food',
        recipient_id: 'user-456',
        status: 'accepted',
        quantity: 10,
        quantity_unit: 'items',
        created_at: new Date(Date.now() - 7200000).toISOString(),
        updated_at: new Date(Date.now() - 3600000).toISOString()
      },
      {
        id: 'mock-req-3',
        listing_id: 'listing-789',
        listing_title: 'Bread',
        recipient_id: 'user-456',
        status: 'fulfilled',
        quantity: 2,
        quantity_unit: 'loaves',
        created_at: new Date(Date.now() - 86400000).toISOString(),
        updated_at: new Date(Date.now() - 43200000).toISOString()
      }
    ];
    
    return {
      message: 'MOCK DATA: Requests retrieved successfully',
      count: mockRequests.length,
      requests: mockRequests
    };
  },

  /**
   * Get requests for the authenticated donor's listings
   * @param {Object} params - Query parameters (limit, offset)
   * @returns {Promise} - API response with requests
   */
  getMyDonorRequests: async (params = {}) => {
    console.log('[requestService] Attempting to fetch donor requests');
    console.log('[requestService] Current user role:', localStorage.getItem('userRole') || 'Not set');
    
    // IMPORTANT: Our axios instance already has baseURL set to 'http://localhost:8787/api'
    // So we should NOT include the '/api' prefix in our paths
    const pathsToTry = [
      '/requests/donor',                // Working endpoint - prioritizing this based on server logs
      '/requests/my-listings-requests',  // Non-working endpoint (404)
      '/test/donor-requests',           // Debug testing endpoint
      'my-listings-requests'            // Direct path for legacy support
    ];
    
    console.log('[requestService] Will try these paths:', pathsToTry);
    
    // Try each path until one works
    for (let i = 0; i < pathsToTry.length; i++) {
      const path = pathsToTry[i];
      try {
        console.log(`Attempting to fetch donor requests from: ${path}`);
        const response = await api.get(path, { params });
        console.log(`Successfully fetched donor requests from: ${path}`);
        // Remember this successful path for future calls
        requestService.donorRequestsPath = path;
        return response.data;
      } catch (pathError) {
        console.log(`Path ${path} failed with:`, pathError.message);
        // Continue to next path
      }
    }
    
    // If all paths failed, use mock data as fallback
    console.error('Error fetching donor requests: All paths failed');
      
    // DEVELOPMENT FALLBACK: Return mock data when API fails
    // This allows frontend development to continue when backend is not ready
    console.info('Using mock data for donor requests as fallback');
    const mockRequests = [
      {
        id: 'mock-donor-req-1',
        listing_id: 'listing-001',
        listing_title: 'Rice and Beans',
        recipient_id: 'user-789',
        recipient_name: 'Local Shelter',
        status: 'pending',
        quantity: 15,
        quantity_unit: 'kg',
        created_at: new Date(Date.now() - 7200000).toISOString(),
        updated_at: new Date(Date.now() - 7200000).toISOString()
      },
      {
        id: 'mock-donor-req-2',
        listing_id: 'listing-002',
        listing_title: 'Fresh Fruit',
        recipient_id: 'user-456',
        recipient_name: 'Community Center',
        status: 'accepted',
        quantity: 8,
        quantity_unit: 'kg',
        created_at: new Date(Date.now() - 14400000).toISOString(),
        updated_at: new Date(Date.now() - 3600000).toISOString()
      },
      {
        id: 'mock-donor-req-3',
        listing_id: 'listing-003',
        listing_title: 'Canned Goods',
        recipient_id: 'user-123',
        recipient_name: 'Food Bank',
        status: 'fulfilled',
        quantity: 20,
        quantity_unit: 'items',
        created_at: new Date(Date.now() - 259200000).toISOString(),
        updated_at: new Date(Date.now() - 86400000).toISOString()
      }
    ];
    
    return {
      message: 'MOCK DATA: Donor requests retrieved successfully',
      count: mockRequests.length,
      requests: mockRequests
    };
  },

  /**
   * Get all requests across the platform (admin only)
   * @param {Object} params - Query parameters (limit, offset, status)
   * @returns {Promise} - API response with all platform requests
   */
  getAdminRequests: async (params = {}) => {
    console.log('[requestService] Attempting to fetch admin requests with params:', params);
    
    // Try different possible API paths
    const pathsToTry = [
      '/admin/requests',         // Standard path
      '/api/admin/requests'      // Alternative with /api prefix
    ];
    
    for (let path of pathsToTry) {
      try {
        console.log(`[requestService] Trying path: ${path}`);
        const response = await api.get(path, { params });
        console.log('[requestService] Admin requests response:', response.data);
        return response.data;
      } catch (pathError) {
        if (pathError.response?.status === 404) {
          console.log(`[requestService] Path ${path} returned 404, trying next path`);
          continue;
        }
        console.error(`[requestService] Error with path ${path}:`, pathError);
        // If it's not a 404 error, rethrow
        if (pathError.response?.status !== 404) {
          throw pathError;
        }
      }
    }
    
    // If we've tried all paths and none worked, use demo data
    console.warn('[requestService] All API paths failed, using demo data');
    return requestService.getDemoRequests(params.status);
  },

  /**
   * Get a request by ID (admin only)
   * @param {string} id - Request ID
   * @returns {Promise} - API response with request details
   */
  getAdminRequestById: async (id) => {
    console.log(`[requestService] Attempting to fetch admin request details for ID: ${id}`);
    
    // Try different possible API paths
    const pathsToTry = [
      `/admin/requests/${id}`,        // Standard path
      `/api/admin/requests/${id}`     // Alternative with /api prefix
    ];
    
    for (let path of pathsToTry) {
      try {
        console.log(`[requestService] Trying path: ${path}`);
        const response = await api.get(path);
        console.log('[requestService] Admin request details response:', response.data);
        
        // If the response has a nested 'request' property, return that
        if (response.data && response.data.request) {
          return response.data;
        } else if (response.data && response.data.results) {
          // Handle D1 database response format
          return {
            message: response.data.message || 'Request details retrieved successfully',
            request: response.data.results[0] || null
          };
        } else {
          return response.data;
        }
      } catch (pathError) {
        if (pathError.response?.status === 404) {
          console.log(`[requestService] Path ${path} returned 404, trying next path`);
          continue;
        }
        console.error(`[requestService] Error with path ${path}:`, pathError);
        // If it's not a 404 error, rethrow
        if (pathError.response?.status !== 404) {
          throw pathError;
        }
      }
    }
    
    // All paths failed, throw an error
    throw new Error('Failed to fetch request details from any endpoint');
  },

  /**
   * Admin approve a request
   * @param {string} id - Request ID
   * @returns {Promise} - API response
   */
  adminApproveRequest: async (id) => {
    console.log(`[requestService] Attempting to approve request ID: ${id}`);
    
    // Try different possible API paths
    const pathsToTry = [
      `/admin/requests/${id}/approve`,       // Standard path
      `/api/admin/requests/${id}/approve`    // Alternative with /api prefix
    ];
    
    for (let path of pathsToTry) {
      try {
        console.log(`[requestService] Trying path: ${path}`);
        const response = await api.put(path);
        console.log('[requestService] Approval response:', response.data);
        return response.data;
      } catch (pathError) {
        if (pathError.response?.status === 404) {
          console.log(`[requestService] Path ${path} returned 404, trying next path`);
          continue;
        }
        console.error(`[requestService] Error with path ${path}:`, pathError);
        // If it's not a 404 error, rethrow
        if (pathError.response?.status !== 404) {
          throw pathError;
        }
      }
    }
    
    // All paths failed, throw an error
    throw new Error('Failed to approve request through any endpoint');
  },

  /**
   * Admin reject a request
   * @param {string} id - Request ID
   * @param {Object} data - Optional rejection reason
   * @returns {Promise} - API response
   */
  adminRejectRequest: async (id, data = {}) => {
    console.log(`[requestService] Attempting to reject request ID: ${id} with reason:`, data);
    
    // Try different possible API paths
    const pathsToTry = [
      `/admin/requests/${id}/reject`,       // Standard path
      `/api/admin/requests/${id}/reject`    // Alternative with /api prefix
    ];
    
    for (let path of pathsToTry) {
      try {
        console.log(`[requestService] Trying path: ${path}`);
        const response = await api.put(path, data);
        console.log('[requestService] Rejection response:', response.data);
        return response.data;
      } catch (pathError) {
        if (pathError.response?.status === 404) {
          console.log(`[requestService] Path ${path} returned 404, trying next path`);
          continue;
        }
        console.error(`[requestService] Error with path ${path}:`, pathError);
        // If it's not a 404 error, rethrow
        if (pathError.response?.status !== 404) {
          throw pathError;
        }
      }
    }
    
    // All paths failed, throw an error
    throw new Error('Failed to reject request through any endpoint');
  },
  
  /**
   * Get demo request data for development
   * @param {string} status - Optional filter by status
   * @returns {Object} - Mock request data
   */
  getDemoRequests: (status) => {
    console.log('[requestService] Generating demo requests with status filter:', status);
    
    const mockRequests = [
      {
        id: 'req_001',
        listingId: 'list_001',
        listing: {
          title: 'Fresh Vegetables Assortment',
          category: 'Produce',
          quantity: '15kg',
          expiry: '2023-08-20'
        },
        donor: {
          id: 'donor_001',
          name: 'FreshGreens Market',
          location: 'Nairobi Central'
        },
        recipient: {
          id: 'recip_001',
          name: 'Hope Children Home',
          organization: 'Hope Foundation'
        },
        quantity: '10kg',
        status: 'pending',
        createdAt: '2023-08-12T10:30:00Z',
        notes: 'Weekend pickup preferred'
      },
      {
        id: 'req_002',
        listingId: 'list_002',
        listing: {
          title: 'Bread and Pastries',
          category: 'Bakery',
          quantity: '25 items',
          expiry: '2023-08-15'
        },
        donor: {
          id: 'donor_002',
          name: 'Daily Bake Shop',
          location: 'Westlands'
        },
        recipient: {
          id: 'recip_002',
          name: 'Street Families Rescue',
          organization: 'Urban Relief NGO'
        },
        quantity: '25 items',
        status: 'approved',
        createdAt: '2023-08-11T08:20:00Z',
        pickupTime: '2023-08-14T10:00:00Z',
        notes: 'Will collect early morning'
      },
      {
        id: 'req_003',
        listingId: 'list_003',
        listing: {
          title: 'Rice and Beans Surplus',
          category: 'Dry Goods',
          quantity: '50kg',
          expiry: '2024-01-15'
        },
        donor: {
          id: 'donor_003',
          name: 'Metro Wholesale',
          location: 'Industrial Area'
        },
        recipient: {
          id: 'recip_003',
          name: 'Community Outreach Center',
          organization: 'Faith Community Services'
        },
        quantity: '30kg',
        status: 'rejected',
        createdAt: '2023-08-10T14:45:00Z',
        rejectionReason: 'Request quantity exceeds policy limits'
      }
    ];
    
    // Filter by status if provided
    const filteredRequests = status ? mockRequests.filter(req => req.status === status) : mockRequests;
    
    return {
      message: 'DEMO DATA: Requests retrieved successfully',
      pagination: {
        total: filteredRequests.length,
        page: 1,
        limit: 10,
        totalPages: 1
      },
      requests: filteredRequests
    };
  }
};

export default requestService;
